/**
 * Admin-preview request-auth resolver for the shared BaseService. Mirrors membership-hmi-preview
 * PR #1321 `applyAdminV2PreviewAuth`: a valid IAM v2 admin-preview session (`acatv2`, role
 * `admin`, unexpired) authenticates courses/membership calls with the admin token + server-minted
 * location scope instead of the learner token.
 *
 * Reads the session via the COMMON `admin-preview-token.utils` only — no import of the courses
 * admin-preview layer or auth store — so this stays dependency-free for every app
 * (GoKollab/Meet/courses) and falls back to the learner path (BaseService) on SSR, where the
 * session read returns null.
 */
import {
  getAdminV2PreviewAccessToken,
  getAdminV2PreviewLocationId,
  readAdminV2PreviewEnvelope,
  type AdminV2PreviewEnvelope,
} from '../../core-layer/utils/admin-preview-token.utils'

/** Backend API segments admin preview is scoped to (courses + membership backends only). */
const ADMIN_PREVIEW_API_SEGMENTS = ['/courses/', '/membership'] as const

/**
 * Whether a request URL is in courses/membership scope for admin preview. Excludes
 * `/communities/` and other backends so the agency-scoped admin token can't leak outside this surface.
 */
export function isCoursesApiRequest(url: string | null | undefined): boolean {
  if (!url) return false
  return ADMIN_PREVIEW_API_SEGMENTS.some(segment => url.includes(segment))
}

export interface ResolvedAdminPreviewAuth {
  /** True only when a valid admin session exists AND the request is in courses/membership scope. */
  isAdmin: boolean
  /** IAM v2 admin access token to attach, or null when not an admin-preview request. */
  token: string | null
  /** Server-minted location id from the exchange envelope, or null. */
  locationId: string | null
}

/**
 * Resolve whether the IAM v2 admin-preview token should authenticate this request. Not gated on
 * `is_preview`/`adminMode` — a valid `acatv2` admin session is authoritative. `isAdmin: false`
 * means BaseService should keep the learner auth.
 *
 * @param requestUrl full request URL.
 * @param envelope pre-resolved envelope; when omitted, reads the client `acatv2` sessionStorage entry (null on SSR).
 */
export function resolveRequestAuth(
  requestUrl: string,
  envelope: AdminV2PreviewEnvelope | null = readAdminV2PreviewEnvelope()
): ResolvedAdminPreviewAuth {
  const token = getAdminV2PreviewAccessToken(envelope)
  if (!token) return { isAdmin: false, token: null, locationId: null }
  if (!isCoursesApiRequest(requestUrl)) return { isAdmin: false, token: null, locationId: null }
  return { isAdmin: true, token, locationId: getAdminV2PreviewLocationId(envelope) }
}
